import { Block } from "@components/Block/Block";
import { FootnotesSection } from "@components/FootnotesSection/FootnotesSection";
import { JWMTableOfContents } from "@components/JWMTableOfContents/JWMTableOfContents";
import { SectionImpression } from "@components/SectionImpression/SectionImpression";
import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { CMSBlock } from "@jwm/types/CMSBlock";
import { ApplicationState } from "@redux/modules/index";
import { default as cmsMapper } from "@utils/cms-mapper";
import classnames from "classnames";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import style from "./style.module.scss";

type PropTypes = {
    body: CMSBlock[];
    placement?: string;
    show_toc?: boolean;
    hasHeader?: boolean;
    headerImageSize?: "X-Small" | "Small" | "Medium" | "Large";
    siteSpecialty?: {
        type: string;
        title: string;
    };
    noTopPaddingFirstSection?: boolean;
};

/**
 * Body is a composite component that renders an array of CMSBlocks as block components underneath each other.
 */
export const Body = (props: PropTypes): JSX.Element => {
    const {
        body,
        placement = "none",
        show_toc,
        hasHeader,
        headerImageSize,
        noTopPaddingFirstSection,
        siteSpecialty,
    } = props;
    const locale = useLocale();
    const footnotes = useSelector((state: ApplicationState) => state.footnotes);

    useEffect(() => {
        window.dispatchEvent(new Event("bodyLoaded"));
    });

    return (
        <div
            className={classnames(
                style.body,
                hasHeader ? style.header : style.noHeader,
                headerImageSize === "X-Small" || headerImageSize === "Small" ? style.smallHeader : style.largeHeader,
            )}
            data-test-id="body"
            id="bodycontent"
        >
            {show_toc && <JWMTableOfContents content={body} />}
            {!body?.length ||
                body.map((block: CMSBlock, index: number) => {
                    // 2 consecutive sections with the same background color should not have the double spacing in between
                    const sectionBackground = cmsMapper.rebrandedBackgroundMap.get(block?.section?.background_color);
                    const nextSectionBackground =
                        !!body[index + 1] &&
                        cmsMapper.rebrandedBackgroundMap.get(body[index + 1]?.section?.background_color);
                    const noBottomSpacing = sectionBackground === nextSectionBackground;

                    return (
                        <div key={index} className={block.section ? null : style.element}>
                            <SectionImpression name={block?.section?.section_title} position={index + 1}>
                                <Block
                                    content={block}
                                    placement={placement}
                                    locale={locale}
                                    index={index}
                                    siteSpecialty={siteSpecialty}
                                    noBottomSpacing={noBottomSpacing}
                                    noTopPaddingFirstSection={noTopPaddingFirstSection}
                                />
                            </SectionImpression>
                        </div>
                    );
                })}
            {footnotes && footnotes.length > 0 && <FootnotesSection placement={placement} />}
        </div>
    );
};

export default Body;
